// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  // JSON_BODY_PATHS endpoints (see `server/apiRouter.js`) take plain JSON.
  const shouldStringifyBody = headers?.['Content-Type'] === 'application/json' && body;
  const bodyMaybe = shouldSerializeBody
    ? { body: serialize(body) }
    : shouldStringifyBody
    ? { body: JSON.stringify(body) }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      // Read as text first. An error response is not always JSON: anything in
      // front of the app — a CDN, a load balancer — can replace a 5xx body with
      // its own HTML error page. `res.json()` on that throws a SyntaxError,
      // which loses the status code and leaves the caller with no idea what
      // happened. That is exactly how a Cloudflare 502 turned into an
      // "Unexpected token '<'" with an empty error message on screen.
      return res.text().then(text => {
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          data = {
            error: `Request failed (${res.status})`,
            status: res.status,
            // Kept short: enough to recognise an HTML error page in a log,
            // not enough to bury it.
            rawBody: text.slice(0, 300),
          };
        }
        let e = new Error(data.error || `Request failed (${res.status})`);
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// A buyer reached checkout for an item whose seller has no payout details yet
// (P25028-195). The server saves the attempt and emails the seller, at most once a day.
//
// See `server/api/payout-nudge.js`. Body: `{ listingId }`.
export const sendPayoutNudge = body => {
  return post('/api/payout-nudge', body);
};

// A message was sent or an order moved on: the server's push and unread jobs read
// Sharetribe's event log soon instead of waiting out a quiet spell. Fire-and-forget: a
// failed nudge only means the push comes on the job's next regular read.
//
// See `server/api/events-nudge.js`.
export const nudgeEventJobs = () => {
  try {
    return post('/api/events/nudge', {}).catch(() => null);
  } catch (e) {
    // No fetch (old browser, tests): nothing to do.
    return Promise.resolve(null);
  }
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const signupWithRecaptcha = body => {
  return post('/api/signup-with-recaptcha', body);
};

export const updateOfferPrice = body => {
  return post('/api/update-offer-price', body);
};

// One page of the main feed in the Recommended order (P25028-237), answered from the
// server's feed cache when it can. Body: the same listing query parameters the page
// would send to Sharetribe. Resolves to the same shape as `sdk.listings.query`.
//
// See `server/api/feed-listings.js`.
export const fetchFeedListings = params => {
  return post('/api/feed-listings', { params }).then(data => ({ data }));
};

// Gives a listing its feed scores right after the seller publishes it, and says whether
// it took one of the seller's two daily top spots (P25028-237). Body: `{ listingId }`.
// Resolves to `{ topSpot: boolean }`, or `{ pending: true }` while it awaits approval.
//
// See `server/api/feed-first-publish.js`.
export const feedFirstPublish = body => {
  return post('/api/feed-first-publish', body);
};

export const postBulkUploadListings = body => {
  return post('/api/bulk-upload', body);
};

export const getBulkUploadListings = body => {
  return get('/api/bulk-upload', body);
};

export const postMailerLite = body => {
  return post('/api/mailer-lite', body);
};

export const postContactRequest = body => {
  return post('/api/contact-request', body);
};
export const postInquiryRequest = body => {
  return post('/api/inquiry-request', body);
};
export const updateUserProfileGeneral = body => {
  return post('/api/update-user-profile', body);
};

// The signed-in user's own referral code, created on the first call (P25028-80).
//
// See `server/api/referral-code.js`.
export const getReferralCode = () => {
  return get('/api/referral/code');
};

// Record "this new account was referred by the owner of this code".
//
// Called once a signup has succeeded. The endpoint always answers 200, so a bad
// or unknown code is not an error here either.
//
// See `server/api/referral-record.js`.
export const postReferralRecord = body => {
  return post('/api/referral/record', body);
};

// Every referral, for the admin screen (P25028-97).
//
// `params` takes { page, perPage, filter }. Answers 404 to anyone who is not an
// admin.
//
// See `server/api/referral-list.js`.
export const getReferralList = (params = {}) => {
  const query = Object.entries(params)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return get(`/api/referral/list${query ? `?${query}` : ''}`);
};

// An admin's decision on a flagged referral: { id, reviewStatus }.
//
// See `server/api/referral-review.js`.
export const postReferralReview = body => {
  return post('/api/referral/review', body);
};

// The partner calls below are parsed by the server as plain JSON rather than
// Sharetribe's Transit format (see the JSON_BODY_PATHS note in
// `server/apiRouter.js`), so the Content-Type is set explicitly here instead
// of falling back to `request()`'s transit+json default.
const JSON_CONTENT_TYPE = { 'Content-Type': 'application/json' };

// Turn the seller's manual partner connection on. Answers { connected,
// status, tokenLastFour, createdAt, issuedAt, expiresAt, revokedAt,
// lastUsedAt, token }. `token` is the raw value, returned only from this
// call, exactly once.
//
// See `server/api/partner-connect.js`.
export const postPartnerConnect = () => {
  return post('/api/partner/connect', undefined, { headers: JSON_CONTENT_TYPE });
};

// Revoke one of the seller's partner connections. Optional body
// { connectionId } revokes that connection; with no body, the seller's manual
// connection is revoked. Idempotent for the manual connection. Answers
// { connected, status, tokenLastFour, createdAt, issuedAt, expiresAt,
// revokedAt, lastUsedAt } for the revoked record.
//
// See `server/api/partner-disconnect.js`.
export const postPartnerDisconnect = body => {
  return post('/api/partner/disconnect', body, { headers: JSON_CONTENT_TYPE });
};

// Every partner connection of the signed-in seller: { connected, connections:
// [{ id, source, clientName, connected, status, tokenLastFour, createdAt,
// issuedAt, expiresAt, revokedAt, lastUsedAt }] }, active ones first. Never
// includes a raw token.
//
// See `server/api/partner-status.js`.
export const getPartnerStatus = () => {
  return get('/api/partner/status');
};

// Checks an OAuth authorize request for the approval page. `params` are the
// page's own query parameters (client_id, redirect_uri, response_type, state,
// ...). Rejects with `error` 'invalid_client' or 'invalid_redirect_uri' when
// the link is not valid; otherwise answers { clientName } or { redirectTo }.
//
// See `server/api/partner-oauth-authorize-check.js`.
export const getPartnerOAuthAuthorize = (params = {}) => {
  const query = Object.entries(params)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return get(`/api/partner/oauth/authorize${query ? `?${query}` : ''}`);
};

// The seller's decision on an OAuth authorize request: the same parameters as
// above plus decision: 'approve' | 'deny'. Answers { redirectTo }.
//
// See `server/api/partner-oauth-authorize.js`.
export const postPartnerOAuthAuthorize = body => {
  return post('/api/partner/oauth/authorize', body, { headers: JSON_CONTENT_TYPE });
};

// The signed-in user opened this conversation: count all its messages as read (P25028-128).
//
// Body: { transactionId }. Answers { unreadMessages } - the user's updated record.
//
// See `server/api/messages-mark-read.js`.
export const postMarkConversationRead = body => {
  return post('/api/messages/mark-read', body);
};

// Inside the Twinr app: save this phone's push token for the signed-in user, or the
// reason it could not give one.
//
// Body: { token, platform } or { error, platform }. Answers { ok: true }.
//
// See `server/api/push-register.js`.
export const postPushRegister = body => {
  return post('/api/push/register', body);
};

// Inside the Twinr app: the signed-in user is logging out, so this phone stops getting
// their notifications. Must be called before the logout, while the session exists.
//
// Body: { token }. Answers { ok: true }.
//
// See `server/api/push-unregister.js`.
export const postPushUnregister = body => {
  return post('/api/push/unregister', body);
};

// The signed-in user's unread message total, from our own database (P25028-128).
//
// Called on every page change, so it makes no Sharetribe request. `key` is the user's
// unread-count key from their private data. Answers { total, updatedAt }. A 404 carries
// `unknownKey: true`, which means "ask for a key again".
//
// See `server/api/messages-unread-count.js`.
export const getUnreadCount = key => {
  return get(`/api/messages/unread-count?key=${encodeURIComponent(key)}`);
};

// Creates the signed-in user's unread-count key the first time, then returns the same one.
// Answers { key }.
//
// See `server/api/messages-unread-key.js`.
export const postUnreadKey = () => {
  return post('/api/messages/unread-key', {});
};

// The shipping label and tracking details for one order (P25028-86).
//
// Readable by both the buyer and the seller. Answers 200 with `label: null`
// until a label has been printed, and carries `available: false` when no Shippo
// key is configured on the server.
//
// See `server/api/shippo-label.js`.
export const getShippingLabel = transactionId => {
  return get(`/api/shippo/label?transactionId=${encodeURIComponent(transactionId)}`);
};

// Will any carrier collect from the seller's posting address?
//
// Body: { shipFrom? } - the address being typed, or omitted to use the saved one.
// Answers { collects: true|false|null, carrier, service }. null means "could not
// find out", which the caller must not show as "nobody collects".
//
// Signed-in sellers only: it spends a Shippo call and reads the caller's own
// address.
//
// See `server/api/shippo-collection-check.js`.
export const postShippoCollectionCheck = body => {
  return post('/api/shippo/collection-check', body);
};

// Buy a real shipping label for an order. Seller only.
//
// Body: { transactionId, parcel: { weightKg, lengthCm, widthCm, heightCm }, shipFrom? }.
// `shipFrom` is only needed the first time — after that the saved address is
// used (P25028-92).
//
// The parcel keys carry their units because a plain `length` key is destroyed by
// Transit serialisation — see buildParcel in ShippingLabelModal.js.
//
// A 400 carrying `needsShipFromAddress` means "ask the seller for their address
// and try again", not "the request was wrong".
//
// See `server/api/shippo-create-label.js`.
export const postCreateShippingLabel = body => {
  return post('/api/shippo/create-label', body);
};

// No courier will take this parcel, so the seller arranges postage with the buyer
// (P25028-190). Body: { transactionId }. Seller only; doing it twice is harmless.
//
// See `server/api/shippo-arrange-direct.js`.
export const postArrangePostageDirectly = body => {
  return post('/api/shippo/arrange-direct', body);
};

// How many of the signed-in seller's listings offer postage with no parcel size
// (P25028-190). Answers { count }.
//
// See `server/api/listings-missing-parcel-size.js`.
export const getListingsMissingParcelSize = () => {
  return get('/api/listings/missing-parcel-size');
};

// The seller deletes one of their own listings (P25028-236). Body: { listingId }.
// A draft is discarded; any other listing is closed and marked deleted.
//
// See `server/api/delete-listing.js`.
export const postDeleteListing = body => {
  return post('/api/listings/delete', body);
};

// The signed-in user deletes their own account (P25028-266). Body: { currentPassword }.
// Refused with 409 `user-has-unfinished-transactions` while an order is still being paid.
//
// See `server/api/delete-account.js`.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Can the signed-in user and this other user contact each other (P25028-272)?
// Answers { isBlocked, blockedByMe }.
//
// See `server/api/block-status.js`.
export const getBlockStatus = userId => {
  return get(`/api/block-status?userId=${encodeURIComponent(userId)}`);
};

// The postage services this buyer may choose between (P25028-132).
//
// Body: { listingId, shippingDetails } - the buyer's delivery address as checkout
// captured it. Answers { options: [...], offPlatform }.
//
// Each option carries only what the buyer needs to choose: rateId, carrier,
// service, the price they would pay, whether the seller has to drop it off, and
// an estimate in days when the carrier gives one. The carrier's own cost and the
// margin stay on the server.
//
// An empty list with no error means "not enough address yet", not "no carrier".
// `offPlatform: true` means this parcel is too big for any carrier and the two
// sides arrange delivery themselves.
//
// The chosen `rateId` then travels with the payment, where the server matches it
// against its own list — a rate id from the browser is never a price.
//
// See `server/api/shipping-options.js`.
// Has this seller an order still waiting for a label (P25028-132)?
//
// Answers { pending: true|false|null, count }. null means "could not find out",
// which the caller must not show as "no" — the posting address stays locked
// rather than being quietly freed.
//
// See `server/api/shippo-pending-labels.js`.
export const getShippoPendingLabels = () => {
  return get('/api/shippo/pending-labels');
};

export const postShippingOptions = body => {
  return post('/api/shipping-options', body);
};
